import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { RouterOutlet } from "@angular/router";
import { DEFAULT_INTERRUPTSOURCES, Idle } from "@ng-idle/core";
import { OAuthService } from "angular-oauth2-oidc";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import { SessionTimeoutDialogComponent } from "./shared/components/session-timeout-dialog/session-timeout-dialog.component";
import { environment } from "../environments/environment";

const IDLE_SECONDS = environment.IDLE_SECONDS; // 1 hour inactive before warning
const TIMEOUT_SECONDS = environment.TIMEOUT_SECONDS; // 60-second countdown before forced logout

@Component({
  selector: "app-root",
  standalone: true,
  imports: [RouterOutlet],
  template: "<router-outlet />",
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly idle = inject(Idle);
  private readonly oauthService = inject(OAuthService);
  private readonly dialog = inject(MatDialog);

  private dialogRef: MatDialogRef<SessionTimeoutDialogComponent> | null = null;
  private readonly subs = new Subscription();

  constructor() {
    this.idle.setIdle(IDLE_SECONDS);
    this.idle.setTimeout(TIMEOUT_SECONDS);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
  }

  ngOnInit(): void {
    this.subs.add(
      this.idle.onIdleStart.subscribe(() => {
        // Lock out interrupts so mouse/keyboard cannot dismiss the dialog
        this.idle.clearInterrupts();
        this.dialogRef = this.dialog.open(SessionTimeoutDialogComponent, {
          data: { countdown: TIMEOUT_SECONDS },
          disableClose: true,
          width: "540px",
          panelClass: "session-timeout-panel",
          backdropClass: "ig-dialog-backdrop",
        });
        this.dialogRef.afterClosed().subscribe((result: string) => {
          this.dialogRef = null;
          if (result === "stay") {
            this.reset();
          } else {
            this.logout();
          }
        });
      }),
    );

    this.subs.add(
      this.idle.onTimeoutWarning.subscribe((countdown: number) => {
        this.dialogRef?.componentInstance?.updateCountdown(countdown);
      }),
    );

    this.subs.add(
      this.idle.onTimeout.subscribe(() => {
        this.dialogRef?.close();
        this.dialogRef = null;
        this.logout();
      }),
    );

    // Only start watching when the user is authenticated —
    // prevents the dialog from showing on the login / callback pages.
    if (this.oauthService.hasValidAccessToken()) {
      this.reset();
    }

    // Start after a fresh login (token arrives from the PKCE callback)
    this.subs.add(
      this.oauthService.events.subscribe((event) => {
        if (event.type === "token_received" && !this.idle.isRunning()) {
          this.reset();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.idle.stop();
  }

  private reset(): void {
    // Restore interrupt sources before restarting the watcher
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.watch();
  }

  private logout(): void {
    this.oauthService.logOut();
  }
}
